"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { useLanguage } from "@/lib/i18n";

export default function TermsPage() {
  const { language } = useLanguage();

  const content = language === "id" ? {
    title: "Terms of Service",
    subtitle: "Syarat dan ketentuan penggunaan layanan premium KinKen Kost Elite.",
    superText: "Rules & Regulations",
    effectiveDate: "Berlaku Mulai: 1 Januari 2026 • Revisi: 1.2",
    importantNote: "<strong>Catatan Penting:</strong> Dengan mengakses website atau melakukan reservasi di KinKen Kost Elite, Anda setuju untuk terikat oleh syarat-syarat premium ini yang mencerminkan standar hospitality kelas dunia.",
    sections: [
      {
        title: "1. Definisi Layanan Premium",
        content: `<strong class="text-brand-gold">KinKen Kost Elite</strong> adalah properti akomodasi premium di Bali yang menyediakan pengalaman living dengan standar hotel bintang 5. Layanan kami mencakup kamar yang lengkap, fasilitas premium, layanan concierge 24/7, dan semua amenities yang ditawarkan sesuai paket yang dipilih.`
      },
      {
        title: "2. Reservasi & Pembayaran Premium",
        content: `<strong>2.1 Minimum Stay:</strong> Masa tinggal minimum 30 hari untuk semua tipe kamar.<br/><br/><strong>2.2 Harga & Pajak:</strong> Semua harga dalam IDR dan belum termasuk:`,
        list: [
          "PPN 11% (Government Tax)",
          "Pajak Daerah 10% (Local Tax)",
          "Service Charge 5% untuk layanan premium"
        ],
        contentAfter: `<strong>2.3 Pembayaran:</strong> Deposit 50% diperlukan untuk mengamankan reservasi, sisanya dibayarkan saat check-in. Kami menerima transfer bank, kartu kredit (Visa/Mastercard), dan digital payment (OVO/GoPay).`
      },
      {
        title: "3. Kebijakan Pembatalan Premium",
        content: `<strong>3.1 Standard Cancellation:</strong><br/>• Lebih dari 30 hari sebelum check-in: Deposit dikembalikan 100%<br/>• 15-30 hari sebelum check-in: Deposit dikembalikan 50%<br/>• Kurang dari 15 hari sebelum check-in: Deposit hangus 100%<br/><br/><strong>3.2 Early Termination:</strong> Pemutusan kontrak sebelum periode berakhir: denda 25% dari sisa kontrak + biaya administrasi.`
      },
      {
        title: "4. House Rules Premium",
        intro: "Sebagai komunitas eksklusif, kami menjaga standar tertinggi:",
        list: [
          "<strong>Quiet Hours:</strong> 22:00 - 07:00 WITA (strictly enforced)",
          "<strong>No Smoking:</strong> Dilarang merokok di semua area dalam ruangan",
          "<strong>Guest Policy:</strong> Tamu maksimal 2 orang per kamar, registrasi wajib di resepsionis",
          "<strong>Pets:</strong> Tidak diperbolehkan (kecuali service animals dengan dokumentasi)",
          "<strong>Security:</strong> Access card wajib digunakan, jangan berikan kepada pihak luar",
          "<strong>Damage/Loss:</strong> Penghuni bertanggung jawab penuh atas kerusakan/perlengkapan hilang"
        ]
      },
      {
        title: "5. Layanan Concierge & Additional Services",
        content: `Layanan concierge 24/7 tersedia untuk membantu dengan:<br/>• Restaurant reservations di fine dining Bali<br/>• Tour & transportation arrangements<br/>• Spa & wellness bookings<br/>• Business center facilities<br/>• Daily housekeeping (included) & laundry service (additional)<br/>• Private chef & in-room dining (additional)`
      },
      {
        title: "6. Liabilitas & Asuransi",
        content: `<strong>6.1 Property Insurance:</strong> Kami memiliki asuransi untuk properti dan fasilitas umum.<br/><br/><strong>6.2 Personal Belongings:</strong> Penghuni bertanggung jawab atas barang pribadi. Safe deposit box tersedia di setiap kamar (gratis).<br/><br/><strong>6.3 Force Majeure:</strong> Tidak bertanggung jawab atas keterlambatan/penundaan karena kondisi di luar kendali (bencana alam, kerusuhan, pandemi).`
      },
      {
        title: "7. Dispute Resolution",
        content: `Setiap sengketa akan diselesaikan secara hierarkis:<br/>1. Negosiasi langsung dengan Management<br/>2. Mediasi melalui Badan Penyelesaian Sengketa Konsumen<br/>3. Pengadilan Negeri Denpasar, Bali<br/><br/>Hukum yang berlaku: <strong>Hukum Republik Indonesia</strong>`
      }
    ],
    contactTitle: "Untuk Klarifikasi & Pertanyaan",
    contactInfo: "KinKen Kost Elite Management<br/>Jl. Tukad Pancoran No.33, Renon, Denpasar Selatan, Bali<br/><strong class=\"text-brand-gold\">legal@kinkenelite.com</strong> • +62 361 123 4567"
  } : {
    title: "Terms of Service",
    subtitle: "Terms and conditions for using KinKen Kost Elite premium services.",
    superText: "Rules & Regulations",
    effectiveDate: "Effective Date: January 1, 2026 • Revision: 1.2",
    importantNote: "<strong>Important Note:</strong> By accessing the website or making a reservation at KinKen Kost Elite, you agree to be bound by these premium terms that reflect world-class hospitality standards.",
    sections: [
      {
        title: "1. Premium Service Definition",
        content: `<strong class="text-brand-gold">KinKen Kost Elite</strong> is a premium accommodation property in Bali providing a living experience with 5-star hotel standards. Our services include fully equipped rooms, premium facilities, 24/7 concierge service, and all amenities offered according to the selected package.`
      },
      {
        title: "2. Reservation & Premium Payment",
        content: `<strong>2.1 Minimum Stay:</strong> Minimum stay of 30 days for all room types.<br/><br/><strong>2.2 Prices & Taxes:</strong> All prices are in IDR and exclude:`,
        list: [
          "11% VAT (Government Tax)",
          "10% Local Tax",
          "5% Service Charge for premium services"
        ],
        contentAfter: `<strong>2.3 Payment:</strong> A 50% deposit is required to secure the reservation, with the balance due at check-in. We accept bank transfers, credit cards (Visa/Mastercard), and digital payments (OVO/GoPay).`
      },
      {
        title: "3. Premium Cancellation Policy",
        content: `<strong>3.1 Standard Cancellation:</strong><br/>• More than 30 days before check-in: 100% deposit refund<br/>• 15-30 days before check-in: 50% deposit refund<br/>• Less than 15 days before check-in: 100% deposit forfeiture<br/><br/><strong>3.2 Early Termination:</strong> Contract termination before the end of the period: 25% penalty of remaining contract + administration fee.`
      },
      {
        title: "4. Premium House Rules",
        intro: "As an exclusive community, we maintain the highest standards:",
        list: [
          "<strong>Quiet Hours:</strong> 22:00 - 07:00 WITA (strictly enforced)",
          "<strong>No Smoking:</strong> Smoking prohibited in all indoor areas",
          "<strong>Guest Policy:</strong> Maximum 2 guests per room, registration required at reception",
          "<strong>Pets:</strong> Not allowed (except service animals with documentation)",
          "<strong>Security:</strong> Access card must be used, do not give to outsiders",
          "<strong>Damage/Loss:</strong> Residents are fully responsible for damage/lost items"
        ]
      },
      {
        title: "5. Concierge & Additional Services",
        content: `24/7 concierge service is available to assist with:<br/>• Restaurant reservations at Bali fine dining<br/>• Tour & transportation arrangements<br/>• Spa & wellness bookings<br/>• Business center facilities<br/>• Daily housekeeping (included) & laundry service (additional)<br/>• Private chef & in-room dining (additional)`
      },
      {
        title: "6. Liability & Insurance",
        content: `<strong>6.1 Property Insurance:</strong> We have insurance for property and common facilities.<br/><br/><strong>6.2 Personal Belongings:</strong> Residents are responsible for personal items. Safe deposit box available in each room (free).<br/><br/><strong>6.3 Force Majeure:</strong> Not responsible for delays due to conditions beyond control (natural disasters, riots, pandemic).`
      },
      {
        title: "7. Dispute Resolution",
        content: `Disputes will be resolved hierarchically:<br/>1. Direct negotiation with Management<br/>2. Mediation through Consumer Dispute Resolution Agency<br/>3. Denpasar District Court, Bali<br/><br/>Governing Law: <strong>Laws of the Republic of Indonesia</strong>`
      }
    ],
    contactTitle: "For Clarification & Questions",
    contactInfo: "KinKen Kost Elite Management<br/>Jl. Tukad Pancoran No.33, Renon, South Denpasar, Bali<br/><strong class=\"text-brand-gold\">legal@kinkenelite.com</strong> • +62 361 123 4567"
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={content.title}
        subtitle={content.subtitle}
        image="https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop"
        superText={content.superText}
      />

      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <div className="prose prose-lg prose-gray max-w-none">
            <p className="text-sm text-gray-400 mb-8 uppercase tracking-widest">
              {content.effectiveDate}
            </p>

            <div className="mb-8 p-4 bg-amber-50 border-l-4 border-brand-gold">
              <p 
                className="text-brand-charcoal"
                dangerouslySetInnerHTML={{ __html: content.importantNote }}
              />
            </div>

            {content.sections.map((section, idx) => (
              <div key={idx}>
                <h3 className="font-serif text-2xl text-brand-charcoal mb-4 mt-8">
                  {section.title}
                </h3>
                
                {section.content && (
                  <p 
                    className="text-gray-500 font-light leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
                
                {section.intro && (
                  <p className="text-gray-500 font-light leading-relaxed mb-4">
                    {section.intro}
                  </p>
                )}
                
                {section.list && (
                  <ul className="list-disc pl-5 space-y-2 text-gray-500 font-light mb-6">
                    {section.list.map((item, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                )}

                {section.contentAfter && (
                  <p 
                    className="text-gray-500 font-light leading-relaxed mb-6"
                    dangerouslySetInnerHTML={{ __html: section.contentAfter }}
                  />
                )}
              </div>
            ))}

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h4 className="font-serif text-xl text-brand-charcoal mb-4">
                {content.contactTitle}
              </h4>
              <p 
                className="text-gray-500"
                dangerouslySetInnerHTML={{ __html: content.contactInfo }}
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
